import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePhotoRefresh } from '../../context/PhotoRefreshContext';
import EmployeeAvatar from '../../components/EmployeeAvatar';
import {
  Card,
  Avatar,
  Descriptions,
  Button,
  Modal,
  Spin,
  Empty,
  message,
  Typography,
  Space,
  Tag,
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  SaveOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import api from '../../utils/api';
import { formatDateTime } from '../../utils/helpers';

const { Title, Text } = Typography;

const ProfileContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ProfileCard = styled(Card)`
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px 12px 0 0;
  margin: -24px -24px 24px -24px;
`;

const ProfileInfo = styled.div`
  flex: 1;
  color: white;
`;

const PhotoGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  margin-top: 16px;
`;

const PhotoItem = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid ${props => props.$selected ? '#667eea' : 'transparent'};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${props => props.$selected && `
    &::after {
      content: '✓';
      position: absolute;
      top: 4px;
      right: 4px;
      background: #667eea;
      color: white;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 14px;
    }
  `}
`;

const Profile = () => {
  const { user } = useAuth();
  const { refreshEmployeePhoto } = usePhotoRefresh();
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employeePhotos, setEmployeePhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [editImageModal, setEditImageModal] = useState(false);
  const [savingPrimaryPhoto, setSavingPrimaryPhoto] = useState(false);
  const [loadingPhotos, setLoadingPhotos] = useState(false);

  // emp_id is the same as username
  const empId = user?.username;

  useEffect(() => {
    if (empId) {
      loadEmployeeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empId]);

  const loadEmployeeData = async () => {
    setLoading(true);
    try {
      const response = await api.searchEmployees(empId);
      setEmployeeData(response);
      setSelectedPhoto(response.primary_photo);
    } catch (error) {
      message.error('Failed to load profile data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUrl = (filename) => {
    if (!filename) return api.getEmployeePhoto(empId);
    const photo = employeePhotos.find(p => p.filename === filename);
    return photo ? photo.url : api.getEmployeePhoto(empId, filename);
  };

  const handleEditImage = async () => {
    setEditImageModal(true);
    setLoadingPhotos(true);

    try {
      const response = await api.getEmployeePhotos(empId);
      // Pre-compute photo URLs
      const photosWithUrls = (response.photos || []).map(photo => ({
        filename: photo,
        url: api.getEmployeePhoto(empId, photo)
      }));
      setEmployeePhotos(photosWithUrls);
      if (response.photos && response.photos.length > 0 && !selectedPhoto) {
        setSelectedPhoto(response.photos[0]);
      }
    } catch (error) {
      message.error('Failed to load photos');
      console.error(error);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const handleSavePrimaryPhoto = async () => {
    if (!selectedPhoto) {
      message.error('Please select a photo first');
      return;
    }

    setSavingPrimaryPhoto(true);
    try {
      await api.setPrimaryPhoto(empId, selectedPhoto);
      message.success('Primary photo updated successfully!');

      // Update employee data
      setEmployeeData({
        ...employeeData,
        primary_photo: selectedPhoto
      });

      // Trigger photo refresh across all components
      refreshEmployeePhoto(empId);

      setEditImageModal(false);
    } catch (error) {
      message.error(error.message || 'Failed to update primary photo');
    } finally {
      setSavingPrimaryPhoto(false);
    }
  };

  if (loading) {
    return (
      <ProfileContainer>
        <Card>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Loading profile...</p>
          </div>
        </Card>
      </ProfileContainer>
    );
  }

  if (!employeeData) {
    return (
      <ProfileContainer>
        <Card>
          <Empty description="Profile data not found" />
        </Card>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <ProfileCard>
        <ProfileHeader>
          <EmployeeAvatar
            empId={empId}
            size={120}
            enablePreview={true}
            photoUpdatedAt={employeeData.photo_updated_at}
            style={{ border: '4px solid white', flexShrink: 0 }}
          />
          <ProfileInfo>
            <Title level={2} style={{ color: 'white', margin: 0 }}>
              {employeeData.full_name}
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 16 }}>
              {employeeData.emp_id}
            </Text>
            {employeeData.email && (
              <div style={{ marginTop: 8 }}>
                <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                  {employeeData.email}
                </Text>
              </div>
            )}
          </ProfileInfo>
          <Button
            icon={<EditOutlined />}
            size="large"
            onClick={handleEditImage}
            style={{ backgroundColor: 'white', borderColor: 'white' }}
          >
            Edit Photo
          </Button>
        </ProfileHeader>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Employee ID">
              {employeeData.emp_id}
            </Descriptions.Item>
            <Descriptions.Item label="Full Name">
              {employeeData.full_name}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {employeeData.email || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Department">
              {employeeData.department ? (
                <Tag color="blue">{employeeData.department}</Tag>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Team">
              {employeeData.team || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={employeeData.active === 'Y' ? 'green' : 'default'}>
                {employeeData.active === 'Y' ? 'Active' : 'Inactive'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>

          <Card title="Recent Activity" size="small">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Space>
                <ClockCircleOutlined style={{ fontSize: 16, color: '#1890ff' }} />
                <div>
                  <Text strong>Last Seen</Text>
                  <br />
                  <Text type="secondary">
                    {employeeData.last_seen ? formatDateTime(employeeData.last_seen) : 'Never'}
                  </Text>
                </div>
              </Space>
              <Space>
                <EnvironmentOutlined style={{ fontSize: 16, color: '#52c41a' }} />
                <div>
                  <Text strong>Location</Text>
                  <br />
                  <Text type="secondary">
                    {employeeData.last_seen_camera_zone || 'Unknown'}
                  </Text>
                </div>
              </Space>
              <Space>
                <ClockCircleOutlined style={{ fontSize: 16, color: '#8c8c8c' }} />
                <div>
                  <Text strong>First Seen</Text>
                  <br />
                  <Text type="secondary">
                    {employeeData.first_seen ? formatDateTime(employeeData.first_seen) : 'Never'}
                  </Text>
                </div>
              </Space>
            </Space>
          </Card>
        </Space>
      </ProfileCard>

      {/* Edit Image Modal */}
      <Modal
        title="Edit Profile Photo"
        open={editImageModal}
        onCancel={() => setEditImageModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setEditImageModal(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            icon={<SaveOutlined />}
            loading={savingPrimaryPhoto}
            onClick={handleSavePrimaryPhoto}
          >
            Save as Primary
          </Button>,
        ]}
        width={600}
      >
        <div>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <Avatar
              key={`modal-${empId}-${selectedPhoto || 'default'}`}
              size={120}
              src={getPhotoUrl(selectedPhoto)}
              icon={<UserOutlined />}
              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            />
            <Title level={5} style={{ marginTop: 16, marginBottom: 4 }}>
              {employeeData.full_name}
            </Title>
            <Text type="secondary">{empId}</Text>
          </div>

          <Text strong style={{ display: 'block', marginBottom: 12 }}>
            Select a photo to set as primary:
          </Text>

          {loadingPhotos ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
            </div>
          ) : employeePhotos.length > 0 ? (
            <PhotoGallery>
              {employeePhotos.map((photo, index) => (
                <PhotoItem
                  key={index}
                  $selected={selectedPhoto === photo.filename}
                  onClick={() => setSelectedPhoto(photo.filename)}
                >
                  <img
                    src={photo.url}
                    alt={`${empId}-${index}`}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/100x100?text=Error';
                    }}
                  />
                </PhotoItem>
              ))}
            </PhotoGallery>
          ) : (
            <Empty description="No photos available" />
          )}
        </div>
      </Modal>
    </ProfileContainer>
  );
};

export default Profile;
